// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { ArrayHelper } from "./array.helper";

export class Tools {
  /**
   * Adds time to a date. Modelled after MySQL DATE_ADD function.
   * Example: dateAdd(new Date(), 'minute', 30)  //returns 30 minutes from now.
   * https://stackoverflow.com/a/1214753/18511
   *
   * @param date  Date to start with
   * @param interval  One of: years, months, weeks, day, hours, minutes, seconds
   * @param units  Number of units of the given interval to add.
   */
  public static dateAdd(date: Date, interval: string, units: number): Date {
    if (!(date instanceof Date)) return undefined;
    let ret = new Date(date); //don't change original date
    const checkRollover = function () {
      if (ret.getDate() != date.getDate()) ret.setDate(0);
    };
    switch (String(interval).toLowerCase()) {
      case "years":
        ret.setFullYear(ret.getFullYear() + units);
        checkRollover();
        break;
      case "months":
        ret.setMonth(ret.getMonth() + units);
        checkRollover();
        break;
      case "weeks":
        ret.setDate(ret.getDate() + 7 * units);
        break;
      case "days":
        ret.setDate(ret.getDate() + units);
        break;
      case "hours":
        ret.setTime(ret.getTime() + units * 3600000);
        break;
      case "minutes":
        ret.setTime(ret.getTime() + units * 60000);
        break;
      case "seconds":
        ret.setTime(ret.getTime() + units * 1000);
        break;
      default:
        ret = undefined;
        break;
    }
    return ret;
  }

  /**
   * Return -1 if date1 < date2
   * Return 1 if date1 > date2
   * Return 0 if date1 === date2
   * @param date1
   * @param date2
   */
  public static compareDates(date1: Date, date2: Date): number {
    if (date1.getTime() < date2.getTime()) {
      return -1;
    } else if (date1.getTime() > date2.getTime()) {
      return 1;
    }
    return 0;
  }

  /**
   * Generate random number of "size" digit
   */
  public static randomNumber(size: number): number {
    const minNb = Math.pow(10, size - 1);
    const maxNb = Math.pow(10, size) - 1;
    return this.randomNumberBetween(minNb, maxNb);
  }

  /**
   * Generate random number between two numbers
   */
  public static randomNumberBetween(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min) + min);
  }

  /**
   * Convert base64 string to JSON object
   * @param b64
   */
  public static base64ToJson(b64: string): unknown {
    try {
      const d = Buffer.from(b64, "base64");
      const t = d.toString("ascii");
      const p = JSON.parse(t);
      return p;
      // return JSON.parse(Buffer.from(b64, "base64").toString("ascii"));
    } catch (err) {
      console.error("base64ToJson", err);
      return null;
    }
  }

  /**
   * Convert JSON object to base64 string
   * @param obj
   */
  public static JsonToBase64(obj: unknown): string {
    try {
      return new Buffer(JSON.stringify(obj)).toString("base64");
    } catch (err) {
      console.error("base64ToJson", err);
      return null;
    }
  }

  /**
   * Remove all spaces from a string
   * @param val
   */
  public static removeSpace(val: string): string {
    try {
      return val.replace(/\s+/g, "");
    } catch (err) {
      return "";
    }
  }

  /**
   * Remove non alphanumeric characters
   * @param val
   */
  public static removeNonAlphaNumeric(val: string): string {
    try {
      return val.replace(/\W/g, "");
    } catch (err) {
      return "";
    }
  }

  public static clone<T>(object: T): T {
    return JSON.parse(JSON.stringify(object)) as T;
  }

  public static pull<T>(array: T[], key: T): void {
    const index = array.indexOf(key, 0);
    if (index > -1) {
      array.splice(index, 1);
    }
  }

  /**
   * Check if it is a valid email
   * @param email
   */
  public static isValidEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    const EMAIL_REGEX =
      // eslint-disable-next-line no-useless-escape -- I don't know if the linter is right... so I'm not taking unknown risks
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return EMAIL_REGEX.test(email);
  }

  /**
   * Check if it is a valid password
   * @param password
   */
  public static isValidPassword(password: string): boolean {
    if (!password) {
      return false;
    }
    const PWD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W]).{8,}$/;
    return PWD_REGEX.test(password);
  }

  /**
   * Check if a string is a valid date
   * @param dateString
   * @returns
   */
  public static isValidDate(dateString: string): boolean {
    return !isNaN(Date.parse(dateString));
  }

  /**
   * Convert a string to a string with the first letter of each word capitalized
   * @param sentence The sentence
   * @returns
   */
  public static startCase(sentence: string): string {
    if (sentence?.length > 0) {
      const words = sentence.trim().toLowerCase().split(" ");
      for (let i = 0; i < words.length; i++) {
        words[i] = words[i][0].toUpperCase() + words[i].slice(1);
      }

      return words.join(" ");
    } else {
      return "";
    }
  }
  /**
   * Check if a value is different of null and undefined
   * @param value
   * @returns
   */
  public static isDefined(value: unknown): boolean {
    return value !== null && value !== undefined;
  }

  /**
   * Check if a value is equal to null or undefined
   * @param value
   * @returns
   */
  public static isNotDefined(value: unknown): boolean {
    return !Tools.isDefined(value);
  }

  /**
   * Check if a array is empty
   * @param value
   * @returns true if empty
   */
  public static isEmpty(value: unknown[]): boolean {
    return Tools.isDefined(value) && value.length === 0;
  }

  public static isFieldString(o: object, field: string): boolean {
    return o && o[field] && typeof o[field] === "string";
  }

  /**
   * The dates in our database contains a '+'
   * this character is changed/deleted when we pass it into a query
   * So we use this method to change this character by '$' before the query
   * @param date
   * @returns a string that contains $ instead of +
   */
  public static convertDateForQuery(date: string): string {
    return date.replace("+", "$");
  }

  /**
   * Since we converted the date to passed it in the query,
   * We use this method to reconvert the date into the right format
   * @param date
   * @returns a string that contains + instead of $
   */
  public static convertDateFromQuery(date: string): string {
    return date.replace("$", "+");
  }

  public static wait(ms: number): Promise<null> {
    return new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, ms);
    });
  }

  public static shuffleString(s: string): string {
    return s
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
  }

  public static getRandomPositiveInt(max: number): number {
    return Math.floor(Math.random() * max) + 1;
  }

  public static uniqCompact(array: string[]): string[] {
    return ArrayHelper.compact([...new Set(array)]);
  }

  public static parseJwt(token: string): unknown {
    if (!token) {
      return "";
    }
    return JSON.parse(Buffer.from(token.split(".")[1], "base64").toString());
  }

  /**
   *
   * @param count
   * @param total
   * @returns count / total in percent, rounded to 2 decimal places. null is returned if total === 0
   */
  public static getFreqInPercent(count: number, total: number): number {
    return total === 0 || Tools.isNotDefined(total) || Tools.isNotDefined(count) ? null : +(100 * (count / total)).toFixed(2);
  }
}
